import {moment} from 'moment'
import * as ko from "knockout";
import {getNextId, getObjectDefinition, IObjectDefinition} from "../../../../../util/misc";
import {ErrorObject} from "../../../../../error/ErrorObject";
import {ICompanySyncstepFilter} from "../../../../../index";
import {IMutableSyncstepFilterParams} from ".";
import {INPUT_DATE_FORMAT} from "../../../../../constants";
import {nsDate} from "../../nsDate";
import {ErrorManager} from "../../../../../error/ErrorManager";

export class MutableSyncstepFilter {
    private readonly objDef: IObjectDefinition
    public readonly _isDeleted = ko.observable(false)

    public readonly ignoresafetylimits: KnockoutObservable<boolean> = ko.observable(false)
    public readonly ignoresecurityfilteruntil: KnockoutObservable<string> = ko.observable('')
    public readonly maxallowedadds: KnockoutObservable<number> = ko.observable(0)
    public readonly maxalloweddeletes: KnockoutObservable<number> = ko.observable(0)
    public readonly maxallowededits: KnockoutObservable<number> = ko.observable(0)

    private eo: Record<
        'ignoresecurityfilteruntil' |
        'maxallowedadds' |
        'maxalloweddeletes' |
        'maxallowededits'
        , ErrorObject> = {
        ignoresecurityfilteruntil: new ErrorObject(),
        maxallowedadds: new ErrorObject(),
        maxalloweddeletes: new ErrorObject(),
        maxallowededits: new ErrorObject(),
    }

    public readonly $original: ICompanySyncstepFilter;
    private componentId = 'filter-' + getNextId()
    private errorManager: ErrorManager;

    constructor(private params: IMutableSyncstepFilterParams) {
        this.errorManager = params.errorManager
        this.objDef = getObjectDefinition(params.filter.objecttypeid)!
        this.$original = params.filter
        this._isDeleted(params.isDeleted)
        this.ignoresafetylimits(this.$original.ignoresafetylimits)
        const ignoresecurityfilteruntil_original = this.$original.ignoresecurityfilteruntil
        const ignoresecurityfilteruntil = ignoresecurityfilteruntil_original
            ? nsDate.convertFromServerString(ignoresecurityfilteruntil_original)
            : moment().add(1, 'days')
        this.ignoresecurityfilteruntil(ignoresecurityfilteruntil.format(INPUT_DATE_FORMAT))
        this.maxallowedadds(this.$original.maxallowedadds)
        this.maxalloweddeletes(this.$original.maxalloweddeletes)
        this.maxallowededits(this.$original.maxallowededits)

        const MinDays = 1;
        const MaxDays = 30;

        this.eo.ignoresecurityfilteruntil.addValidator({
            fn: () => {
                if (!this.ignoresecurityfilteruntil()) {
                    return false
                }
                const m = moment(this.ignoresecurityfilteruntil())
                if (!m.isValid()) {
                    return false
                }
                return true
            },
            message: 'invalid value',
            prerequisite: () => !this._isDeleted() && this.ignoresafetylimits()
        }, {
            fn: () => {
                const m = moment(this.ignoresecurityfilteruntil(), INPUT_DATE_FORMAT)
                const minDate = moment().add(MinDays, 'day').startOf('day');
                const maxDate = moment().add(MaxDays, 'day').endOf('day')

                return m.isBetween(minDate, maxDate, null, '[]')
            },
            message: `date must be between start and end in future|fromdate:${MinDays}|todate:${MaxDays}`,
            prerequisite: () => {
                const a = !this._isDeleted();
                const b = this.ignoresafetylimits();
                return a && b
            }
        })
        this.eo.maxallowedadds.addValidator({
            fn: () => /^\d{1,}$/.test(String(this.maxallowedadds())),
            message: 'invalid value',
            prerequisite: () => !this._isDeleted(),
        })
        this.eo.maxalloweddeletes.addValidator({
            fn: () => /^\d{1,}$/.test(String(this.maxalloweddeletes())),
            message: 'invalid value',
            prerequisite: () => !this._isDeleted(),
        })
        this.eo.maxallowededits.addValidator({
            fn: () => /^\d{1,}$/.test(String(this.maxallowededits())),
            message: 'invalid value',
            prerequisite: () => !this._isDeleted(),
        })

        this.errorManager.addErrorObject(this.eo.ignoresecurityfilteruntil)
        this.errorManager.addErrorObject(this.eo.maxallowedadds)
        this.errorManager.addErrorObject(this.eo.maxalloweddeletes)
        this.errorManager.addErrorObject(this.eo.maxallowededits)
    }

    public isAdded(): boolean {
        if (this.isEdited()) {
            return false
        }
        const a = !this.$original.id
        const b = !this._isDeleted()
        return a && b
    }

    public isEdited(): boolean {
        if (!this.$original.id || this._isDeleted()) {
            return false
        }
        const data = this.getSubmitData()
        const a = data.ignoresafetylimits != this.$original.ignoresafetylimits
        const b = data.ignoresecurityfilteruntil != this.$original.ignoresecurityfilteruntil
        const c = data.maxallowedadds != this.$original.maxallowedadds
        const d = data.maxalloweddeletes != this.$original.maxalloweddeletes
        const e = data.maxallowededits != this.$original.maxallowededits
        return a || b || c || d || e
    }

    public isDeleted(): boolean {
        if (this.isEdited()) {
            return false
        }
        const a = this._isDeleted()
        const b = !!this.$original.id
        return a && b
    }

    public getSubmitData(): ICompanySyncstepFilter {
        const ignoresecurityfilteruntil = this.ignoresecurityfilteruntil()
            ? nsDate.convertToServerString(moment(this.ignoresecurityfilteruntil(), INPUT_DATE_FORMAT))
            : nsDate.convertToServerString(moment()) // API demands a date! ¯\_(ツ)_/¯
        return {
            companysyncstepid: this.$original.companysyncstepid,
            id: this.$original.id,
            ignoresafetylimits: this.ignoresafetylimits(),
            ignoresecurityfilteruntil: ignoresecurityfilteruntil,
            maxallowedadds: Number(this.maxallowedadds()),
            maxalloweddeletes: Number(this.maxalloweddeletes()),
            maxallowededits: Number(this.maxallowededits()),
            objecttypeid: this.$original.objecttypeid,
        }
    }

    private isChecked = ko.computed({
        read: () => !this._isDeleted(),
        write: (bool: boolean) => this._isDeleted(!bool)
    })

    public isValid(): boolean {
        const a = this.eo.ignoresecurityfilteruntil.validate()
        const b = this.eo.maxallowedadds.validate()
        const c = this.eo.maxalloweddeletes.validate()
        const d = this.eo.maxallowededits.validate()
        return a && b && c && d
    }

    private getInputClass(eo: ErrorObject): string {
        return eo.validate()
            ? ''
            : 'error'
    }

    dispose() {
        this.isChecked.dispose()
        this.errorManager.removeErrorObject(this.eo.ignoresecurityfilteruntil)
        this.errorManager.removeErrorObject(this.eo.maxallowedadds)
        this.errorManager.removeErrorObject(this.eo.maxalloweddeletes)
        this.errorManager.removeErrorObject(this.eo.maxallowededits)
    }

}
